import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Crown, Pencil, SlidersHorizontal, X } from 'lucide-react';
import { t } from '../i18n';
import { localizeError } from '../i18n/errors';
import { QRCodeSVG } from 'qrcode.react';
import { isCurrencyLocked, isValidBetAmount } from '../utils/currency';
import { useCurrency } from '../utils/currencyContext';
import { assignRoles } from '../utils/pokerLogic';
import { removePlayer, setLobbyBuyIn, setRoomCurrency, setTableBuyIn } from '../utils/roomManager';
import { seatedPlayers, tableDefaultBuyIn } from '../utils/playerLedger';
import { shareInvite } from '../utils/inviteShare';
import { useOverflowing, useVisualViewport } from '../utils/visualViewport';
import CurrencyModal from './CurrencyModal';
import { RemovePlayerConfirm } from './game/RemovePlayer';
import OrderSheet from './OrderSheet';
import Button from './ui/Button';
import Wordmark from './ui/Wordmark';
import Modal from './ui/Modal';
function chipsTitle(name) {
  return t('lobby.chipsTitle', { name });
}

function LobbyOverlay({
  gameState,
  currentPlayerId,
  isHost,
  onStartGame,
  onOpenSettings,
  onLeaveRoom,
  error
}) {
  const { roomCode, settings, players, hostId } = gameState;
  const { formatCurrency, currency, symbol, parseCurrencyInput, centsToInputString, amountInputProps } = useCurrency();
  const vv = useVisualViewport();
  const shellRef = useRef(null);
  const playerListRef = useRef(null);

  const approvedPlayers = seatedPlayers(players);
  const maxPlayers = settings?.maxPlayers || 10;
  const dense = approvedPlayers.length > 6;
  const rowMin = dense ? 40 : 48;
  const listOverflowing = useOverflowing(playerListRef, [approvedPlayers.length, dense]);

  const [showCurrency, setShowCurrency] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [qrExpanded, setQrExpanded] = useState(false);
  const [showOrderSheet, setShowOrderSheet] = useState(false);
  const [orderedPlayers, setOrderedPlayers] = useState([]);
  const [startingGame, setStartingGame] = useState(false);
  const approvedIdKey = approvedPlayers.map((p) => p.id).join(',');

  const [editTarget, setEditTarget] = useState(null);
  const [editTable, setEditTable] = useState(false);
  const [editAmount, setEditAmount] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const defaultBuyIn = tableDefaultBuyIn(players, hostId);
  const [glowIds, setGlowIds] = useState(() => new Set());
  const seenIdsRef = useRef(new Set(approvedPlayers.map((p) => p.id)));

  useEffect(() => {
    const nextIds = new Set(approvedPlayers.map((p) => p.id));
    const fresh = approvedPlayers.filter((p) => !seenIdsRef.current.has(p.id)).map((p) => p.id);
    seenIdsRef.current = nextIds;
    if (fresh.length === 0) return undefined;
    setGlowIds((prev) => {
      const merged = new Set(prev);
      fresh.forEach((id) => merged.add(id));
      return merged;
    });
    const glowTimer = setTimeout(() => {
      setGlowIds((prev) => {
        const copy = new Set(prev);
        fresh.forEach((id) => copy.delete(id));
        return copy;
      });
    }, 1600);
    return () => clearTimeout(glowTimer);
  }, [approvedIdKey, approvedPlayers]);

  const joinLink = typeof window !== 'undefined'
    ? `${window.location.origin}/join/${roomCode}`
    : '';

  const handleShare = async () => {
    try {
      const result = await shareInvite({ url: joinLink });
      if (result === 'copied') {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 1500);
      }
    } catch (err) {
      console.warn('Share failed:', err);
    }
  };

  const handleCancelRemove = () => {
    if (removing) return;
    setRemoveTarget(null);
    setRemoveError('');
  };

  const handleConfirmRemove = async () => {
    if (!isHost || !removeTarget || removing) return;
    setRemoving(true);
    setRemoveError('');
    try {
      await removePlayer(roomCode, removeTarget.id, currentPlayerId);
      setRemoveTarget(null);
    } catch (err) {
      console.error('Failed to remove player:', err);
      setRemoveError(err.message || t('error.couldNotRemove'));
    } finally {
      setRemoving(false);
    }
  };

  const canStartGame = approvedPlayers.length >= 2 && isHost;

  useEffect(() => {
    if (!showOrderSheet) return;
    const approved = seatedPlayers(players);
    setOrderedPlayers((prev) => {
      const approvedById = new Map(approved.map((p) => [p.id, p]));
      const kept = prev
        .filter((p) => approvedById.has(p.id))
        .map((p) => approvedById.get(p.id));
      const keptIds = new Set(kept.map((p) => p.id));
      const added = approved.filter((p) => !keptIds.has(p.id));
      const next = [...kept, ...added];
      const unchanged =
        next.length === prev.length &&
        next.every((p, i) => p === prev[i]);
      return unchanged ? prev : next;
    });
  }, [showOrderSheet, approvedIdKey, players]);

  const handleOpenOrderSheet = () => {
    if (!canStartGame) return;
    setOrderedPlayers(approvedPlayers);
    setShowOrderSheet(true);
  };

  const handleCancelOrder = () => {
    if (startingGame) return;
    setShowOrderSheet(false);
    setOrderedPlayers([]);
  };

  const handleConfirmStart = async () => {
    if (orderedPlayers.length < 2 || startingGame) return;
    setStartingGame(true);
    try {
      await onStartGame(orderedPlayers.map((p) => p.id));
    } finally {
      setStartingGame(false);
    }
  };

  let orderRoleAssignments = {};
  if (orderedPlayers.filter((p) => (p.stack || 0) > 0).length >= 2) {
    try {
      orderRoleAssignments = assignRoles(orderedPlayers, null).roleAssignments;
    } catch {
      orderRoleAssignments = {};
    }
  }

  const handleLeaveClick = () => {
    if (isHost) {
      setShowExitConfirmation(true);
    } else {
      onLeaveRoom();
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirmation(false);
    onLeaveRoom();
  };

  const handleSelectCurrency = async (code) => {
    if (!isHost || !code || code === currency) {
      setShowCurrency(false);
      return;
    }
    try {
      await setRoomCurrency(roomCode, currentPlayerId, code);
      setShowCurrency(false);
    } catch (err) {
      console.error('Failed to set currency:', err);
    }
  };

  const handleOpenStackEdit = (player) => {
    if (!isHost || !player) return;
    setEditTable(false);
    setEditTarget(player);
    setEditAmount(centsToInputString(player.stack));
    setEditError('');
  };

  const handleOpenTableBuyIn = () => {
    if (!isHost) return;
    setEditTable(true);
    setEditTarget({ id: hostId, name: 'Table' });
    setEditAmount(centsToInputString(defaultBuyIn));
    setEditError('');
  };

  const handleCancelStackEdit = () => {
    if (editSaving) return;
    setEditTarget(null);
    setEditTable(false);
    setEditAmount('');
    setEditError('');
  };

  const handleSaveStackEdit = async () => {
    if (!isHost || !editTarget || editSaving) return;
    let amount;
    try {
      amount = parseCurrencyInput(editAmount);
    } catch {
      setEditError(t('error.validAmount'));
      return;
    }
    if (!isValidBetAmount(amount, 100)) {
      setEditError(t('error.minAmount', { amount: formatCurrency(100) }));
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      const outcome = editTable
        ? await setTableBuyIn(roomCode, currentPlayerId, amount)
        : await setLobbyBuyIn(roomCode, currentPlayerId, {
          playerId: editTarget.id,
          amount,
        });
      if (!outcome.ok) {
        setEditError(localizeError(outcome.reason || t('error.couldNotSave')));
        return;
      }
      setEditTarget(null);
      setEditTable(false);
      setEditAmount('');
    } catch (err) {
      console.error('Failed to edit stack:', err);
      setEditError(localizeError(err.message || t('error.couldNotSave')));
    } finally {
      setEditSaving(false);
    }
  };

  const shellStyle = {
    transform: vv.offsetTop ? `translateY(${vv.offsetTop}px)` : undefined,
  };

  const countLabel = (() => {
    if (isHost && approvedPlayers.length < 2) {
      return t('lobby.need2', { count: approvedPlayers.length, max: maxPlayers });
    }
    if (!isHost) return t('lobby.hostDeals', { count: approvedPlayers.length, max: maxPlayers });
    return t('lobby.count', { count: approvedPlayers.length, max: maxPlayers });
  })();

  const showEmptySeat = approvedPlayers.length < maxPlayers;

  let slotPrimary;
  let slotSecondary;
  if (isHost) {
    slotPrimary = (
      <Button
        variant="primary"
        size="lg"
        trailingArrow
        onClick={handleOpenOrderSheet}
        disabled={!canStartGame}
        className="flex-1"
        data-testid="btn-start-game"
      >
        {t('lobby.deal')}
      </Button>
    );
    slotSecondary = (
      <Button
        variant="secondary"
        size="lg"
        onClick={handleShare}
        className="flex-1"
        data-testid="lobby-share"
      >
        {copiedLink ? t('common.copied') : t('lobby.shareInvite')}
      </Button>
    );
  } else {
    slotPrimary = (
      <Button
        variant="quiet"
        size="lg"
        disabled
        className="flex-1"
        data-testid="lobby-waiting"
      >
        {t('lobby.waiting')}
      </Button>
    );
    slotSecondary = (
      <Button
        variant="secondary"
        size="lg"
        onClick={handleShare}
        className="flex-1"
        data-testid="lobby-share"
      >
        {copiedLink ? t('common.copied') : t('lobby.shareInvite')}
      </Button>
    );
  }

    const editTitle = editTable ? t('lobby.tableBuyIn') : (editTarget ? chipsTitle(editTarget.name) : '');

  return (
    <div
      ref={shellRef}
      className={`lobby-shell fixed inset-x-0 top-0 h-[100dvh] bg-cream text-ink z-50 flex flex-col overflow-hidden ${dense ? 'lobby-rows-dense' : ''}`}
      style={shellStyle}
      data-testid="lobby"
    >
      <div
        className="lobby-shell-inner flex flex-col min-h-0 flex-1 h-full"
        data-testid="lobby-inner"
      >
      <header className="flex-none flex items-center justify-between px-5 sm:px-8 pt-3 pb-1">
        <Wordmark size="md" />
        <div className="flex items-center gap-2">
          {isHost && (
            <button
              type="button"
              onClick={onOpenSettings}
              aria-label={t('lobby.gameSettings')}
              data-testid="lobby-settings"
              style={{ WebkitTapHighlightColor: 'transparent', boxShadow: 'none' }}
              className="h-[34px] px-3 rounded-full border border-ink bg-cream text-ink text-[13px] font-medium inline-flex items-center gap-1.5 whitespace-nowrap"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" strokeWidth={1.75} aria-hidden />
              <span className="max-w-[42vw] truncate">{t('lobby.gameSettings')}</span>
            </button>
          )}
          <button
            onClick={handleLeaveClick}
            className="p-2 text-mute hover:text-ink transition-colors"
            aria-label={t('lobby.leaveGame')}
            data-testid="lobby-leave"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="relative flex-1 min-h-0 w-full max-w-md mx-auto px-6 sm:px-8 flex flex-col pt-4">
        <section className="lobby-invite flex-none" data-testid="lobby-invite">
            <div className="lobby-invite__label label-eyebrow text-mute">{t('lobby.invitePlayers')}</div>
            <div
              className="lobby-invite__code font-semibold tnum text-ink"
              data-testid="lobby-room-code"
            >
              {roomCode}
            </div>

            {joinLink && (
              <button
                type="button"
                onClick={() => setQrExpanded(true)}
                className="lobby-invite__qr bg-white p-2 rounded-[12px] border border-line hover:border-ink transition-colors"
                aria-label={t('lobby.enlargeQr')}
                data-testid="lobby-qr"
              >
                <QRCodeSVG
                  value={joinLink}
                  size={128}
                  bgColor="#ffffff"
                  fgColor="#111111"
                  level="M"
                  includeMargin={false}
                  style={{ display: 'block', width: '100%', height: '100%' }}
                />
              </button>
            )}

            <div className="lobby-invite__blinds flex items-center gap-2 h-7 text-[13px] text-mute whitespace-nowrap" data-testid="lobby-blinds">
              {isHost ? (
                <button
                  type="button"
                  onClick={handleOpenTableBuyIn}
                  data-testid="lobby-table-buyin"
                  aria-label={t('lobby.editBuyIn')}
                  className="inline-flex items-center gap-1 text-[13px] text-mute bg-transparent p-0 border-0 border-b border-dotted border-line leading-tight"
                >
                  <span className="tnum">{t('lobby.buyIn', { amount: formatCurrency(defaultBuyIn) })}</span>
                  <Pencil className="w-3.5 h-3.5" aria-hidden />
                </button>
              ) : (
                <span className="tnum leading-tight" data-testid="lobby-table-buyin">
                  {t('lobby.buyIn', { amount: formatCurrency(defaultBuyIn) })}
                </span>
              )}
              <span aria-hidden className="text-line">·</span>
              <div>
                <span className="text-ink font-medium">{t('poker.sb')}</span>{' '}
                <span className="tnum">{formatCurrency(settings.smallBlind)}</span>
              </div>
              <span aria-hidden className="text-line">·</span>
              <div>
                <span className="text-ink font-medium">{t('poker.bb')}</span>{' '}
                <span className="tnum">{formatCurrency(settings.bigBlind)}</span>
              </div>
              {isHost && !isCurrencyLocked(gameState) && (
                <>
                  <span aria-hidden className="text-line">·</span>
                  <button
                    type="button"
                    onClick={() => setShowCurrency(true)}
                    data-testid="lobby-currency"
                    className="inline-flex items-center gap-1 h-7 text-[13px] text-mute bg-transparent p-0 border-0"
                  >
                    <span className="tnum">{symbol}</span>
                    <span>{currency}</span>
                    <ChevronDown className="w-3.5 h-3.5" aria-hidden />
                  </button>
                </>
              )}
            </div>
          </section>

        {qrExpanded && joinLink && (
          <button
            type="button"
            onClick={() => setQrExpanded(false)}
            className="fixed inset-0 z-[60] bg-cream flex flex-col items-center justify-center p-6"
            aria-label={t('lobby.closeQr')}
          >
            <div className="bg-white p-4 rounded-2xl border border-line">
              <QRCodeSVG
                value={joinLink}
                size={320}
                bgColor="#ffffff"
                fgColor="#111111"
                level="M"
                includeMargin={false}
              />
            </div>
            <div className="mt-6 text-ink text-5xl font-semibold tnum tracking-[0.18em]">
              {roomCode}
            </div>
            <div className="mt-3 text-mute text-[13px]">{t('lobby.tapToClose')}</div>
          </button>
        )}

        <section
          className="min-h-0 flex flex-col flex-1 mt-4"
          data-testid="lobby-players"
        >
          <div className="flex-none flex items-baseline justify-between mb-2">
            <h3 className="label-eyebrow text-mute">
              {t('lobby.players')}
            </h3>
            <span className="text-[13px] text-mute tnum" data-testid="lobby-count">
              {countLabel}
            </span>
          </div>

          <div className={`lobby-fade flex-1 min-h-0 ${listOverflowing ? 'is-overflowing' : ''}`}>
            <ul
              ref={playerListRef}
              className="lobby-fade__scroller divide-y divide-line-2 border-y border-line-2"
              data-testid="lobby-player-list"
            >
              {approvedPlayers.map((player) => (
                <li
                  key={player.id}
                  className={`flex items-center justify-between gap-2 py-1 ${glowIds.has(player.id) ? 'bg-cream-2' : ''}`}
                  style={{ minHeight: rowMin }}
                >
                  <div className="flex-1 flex items-center gap-3 min-w-0">
                    {(player.isHost || player.id === hostId) && (
                      <Crown className="w-4 h-4 text-ink flex-shrink-0" aria-label={t('common.host')} />
                    )}
                    <div className="flex-1 min-w-0 flex items-baseline justify-between gap-2.5">
                      <div className="flex-1 min-w-0 text-ink font-medium truncate">
                        {player.name}
                        {player.id === currentPlayerId && (
                          <span className="text-mute text-[13px] ml-2">{t('common.you')}</span>
                        )}
                      </div>
                      <div className="flex-none whitespace-nowrap text-mute text-[13px] tnum">
                        {isHost ? (
                          <button
                            type="button"
                            onClick={() => handleOpenStackEdit(player)}
                            className="inline-flex items-center gap-1 text-mute hover:text-ink transition-colors bg-transparent border-0 p-0"
                            aria-label={t('lobby.editChips', { name: player.name })}
                            data-testid={`lobby-edit-stack-${player.id}`}
                          >
                            <span>{centsToInputString(player.stack)}</span>
                            <Pencil className="w-3.5 h-3.5" aria-hidden />
                          </button>
                        ) : (
                          formatCurrency(player.stack)
                        )}
                      </div>
                    </div>
                  </div>

                  {isHost && !player.isHost && player.id !== currentPlayerId && (
                    <button
                      type="button"
                      onClick={() => { setRemoveError(''); setRemoveTarget(player); }}
                      className="p-2 -mr-2 text-mute hover:text-accent-danger transition-colors"
                      aria-label={t('lobby.removeAria', { name: player.name })}
                      data-testid={`lobby-remove-${player.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
              {showEmptySeat && (
                <li className={`lobby-empty-seat ${dense ? 'is-dense' : ''}`} aria-hidden data-testid="lobby-empty-seat" />
              )}
            </ul>
          </div>
        </section>

        {error && (
          <div className="flex-none mt-3 text-[13px] text-accent-danger border border-accent-danger/30 rounded-lg px-3 py-2" data-testid="join-error">
            {localizeError(error)}
          </div>
        )}
      </main>

      <div
        className="flex-none shrink-0 w-full max-w-md mx-auto px-6 sm:px-8 relative z-30 pt-3"
        style={{ paddingBottom: 'max(14px, env(safe-area-inset-bottom))' }}
        data-testid="lobby-slot"
      >
        <div className="flex flex-row gap-3" data-testid={isHost ? 'lobby-start-area' : undefined}>
          {slotPrimary}
          {slotSecondary}
        </div>
      </div>
      </div>

      {isHost && (
        <OrderSheet
          open={showOrderSheet}
          players={orderedPlayers}
          roleAssignments={orderRoleAssignments}
          dense={orderedPlayers.length > 6}
          starting={startingGame}
          error={localizeError(error)}
          onReorder={setOrderedPlayers}
          onDeal={handleConfirmStart}
          onBack={handleCancelOrder}
        />
      )}

      {isHost && (
        <RemovePlayerConfirm
          player={removeTarget}
          busy={removing}
          error={removeError}
          onCancel={handleCancelRemove}
          onConfirm={handleConfirmRemove}
        />
      )}

      {isHost && (
        <Modal
          open={!!editTarget}
          onClose={handleCancelStackEdit}
          dismissible={!editSaving}
          compact
          title={editTitle}
          testId="lobby-stack-edit"
          footer={
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCancelStackEdit}
                disabled={editSaving}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveStackEdit}
                disabled={editSaving || !editAmount}
              >
                {editSaving ? t('common.savingShort') : t('common.save')}
              </Button>
            </>
          }
        >
          <input
            type="text"
            {...amountInputProps}
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            data-testid="lobby-stack-amount"
            className="w-full px-4 py-3 bg-transparent border border-line rounded-lg text-ink tnum text-center text-[22px] tracking-[0.08em] placeholder:text-mute focus:outline-none focus:border-ink transition-colors"
            autoFocus
            disabled={editSaving}
            aria-label={editTitle || 'Chips'}
          />
            {editError && (
            <div className="mt-3 text-[13px] text-accent-danger border border-accent-danger/30 rounded-lg px-3 py-2" role="alert">
              {localizeError(editError)}
            </div>
          )}
        </Modal>
      )}

      {showExitConfirmation && (
        <Modal
          open
          onClose={() => setShowExitConfirmation(false)}
          title={t('lobby.endSessionTitle')}
          footer={
            <>
              <Button variant="secondary" size="sm" onClick={() => setShowExitConfirmation(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" size="sm" onClick={handleConfirmExit}>
                {t('lobby.endGame')}
              </Button>
            </>
          }
        >
          <p className="text-[14px] text-ink-2 leading-relaxed">
            {t('lobby.endSessionBody')}
          </p>
        </Modal>
      )}

      {isHost && (
        <CurrencyModal
          open={showCurrency}
          selected={currency}
          onSelect={handleSelectCurrency}
          onClose={() => setShowCurrency(false)}
        />
      )}
    </div>
  );
}

export default LobbyOverlay;
